<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import { IMenuQrTarget } from '@/domain/menu-qr/menu-qr-targets';

  const props = withDefaults(
    defineProps<{
      imageUrl?: string;
      /** Undefined when there is nothing to preview. */
      current?: IMenuQrTarget;
      index: number;
      total: number;
      /** False while the poster copy is still being resolved. */
      ready: boolean;
      rendering: boolean;
      failed: boolean;
    }>(),
    {
      imageUrl: undefined,
      current: undefined,
    }
  );

  const emit = defineEmits<{
    (e: 'prev'): void;
    (e: 'next'): void;
  }>();

  const proxy = getCurrentInstance()!.proxy;

  const badgeLabel = computed(() =>
    String(
      proxy.$t(
        props.current?.design === 'orders'
          ? 'qr.design.badge.orders'
          : 'qr.design.badge.view'
      )
    )
  );
</script>

<template>
  <div
    class="qr-preview"
    :data-rendering="rendering || (!ready && !failed) ? 'true' : 'false'"
  >
    <!-- Failure before anything could be listed (copy or branch token): no empty-state advice. -->
    <div v-if="failed && total === 0" class="preview-well">
      <p class="preview-failed">{{ $t('qr.preview.failed') }}</p>
    </div>

    <div v-else-if="!ready" class="preview-well">
      <div class="preview-overlay">
        <span class="preview-status">{{ $t('qr.preview.generating') }}</span>
      </div>
    </div>

    <PosUiEmptyState
      v-else-if="total === 0"
      icon="qrcode"
      :title="String($t('qr.preview.empty.title'))"
      :description="String($t('qr.preview.empty.description'))"
    />

    <template v-else>
      <div class="preview-caption" :class="{ 'is-single': total <= 1 }">
        <PosUiButton
          class="pager-button"
          color="neutral"
          variant="ghost"
          size="md"
          icon="chevron-left"
          :title="String($t('qr.preview.previous'))"
          :disabled="index <= 0"
          @click="emit('prev')"
        />

        <div class="caption-text">
          <span class="caption-title">{{
            current ? current.caption : ''
          }}</span>
          <span class="caption-meta">
            <PosUiBadge
              v-if="current"
              :label="badgeLabel"
              :color="current.design === 'orders' ? 'info' : 'neutral'"
              size="xs"
            />
            <span class="caption-position">
              {{ $t('qr.preview.position', { index: index + 1, total }) }}
            </span>
          </span>
        </div>

        <PosUiButton
          class="pager-button"
          color="neutral"
          variant="ghost"
          size="md"
          icon="chevron-right"
          :title="String($t('qr.preview.next'))"
          :disabled="index >= total - 1"
          @click="emit('next')"
        />
      </div>

      <div class="preview-well">
        <img
          v-if="imageUrl"
          class="preview-image"
          :src="imageUrl"
          :data-target-id="current ? current.id : ''"
          :alt="current ? current.caption : ''"
        />
        <div v-show="rendering" class="preview-overlay">
          <span class="preview-status">{{ $t('qr.preview.generating') }}</span>
        </div>
        <p v-if="failed" class="preview-failed">
          {{ $t('qr.preview.failed') }}
        </p>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .qr-preview {
    @apply tw-flex tw-flex-col tw-gap-3;

    .preview-caption {
      @apply tw-flex tw-items-center tw-gap-2;

      &.is-single {
        .pager-button {
          @apply tw-invisible;
        }
      }

      .caption-text {
        @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-items-center tw-gap-0.5 tw-text-center;

        .caption-title {
          @apply tw-w-full tw-truncate tw-text-sm tw-font-semibold tw-text-neutral-900;
        }

        .caption-meta {
          @apply tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-neutral-500;
        }
      }
    }

    .preview-well {
      @apply tw-relative tw-min-h-40 tw-rounded-xl tw-bg-canvas tw-p-4;

      .preview-image {
        @apply tw-mx-auto tw-block tw-h-auto tw-max-w-full tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
        max-height: 42vh;

        @media (min-width: 768px) {
          max-height: none;
          max-width: min(100%, 560px);
        }
      }

      .preview-overlay {
        @apply tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-bg-white/70;

        .preview-status {
          @apply tw-animate-pulse tw-text-sm tw-font-medium tw-text-neutral-600;
        }
      }

      .preview-failed {
        @apply tw-m-0 tw-py-6 tw-text-center tw-text-sm tw-text-red-600;
      }
    }
  }
</style>
